/**
 * +field: the WoW-style XP bar: thin, 20 segments, the level at the left end, "xp / needed (pct)" on
 * hover. Docked inside the skill cluster right above the health / resource bars, at their width (it
 * waits for the cluster to exist, then moves itself there; until then it sits on the bottom edge). Moves on every kill (worldApp predicts the XP); a
 * level-up fills it to the end, flashes and rolls over to the new level's fill.
 *
 * Polls fieldView.xpVersion per frame; touches the DOM only when it changes.
 */
import type { HudWidget } from '../widgets';
import { el, setText } from '../dom';
import { fieldView } from '../../world/fieldView';
import { XP_SEGMENTS, type XpState, xpBarView, xpTween } from '../../world/xpBarModel';

const FILL_MS = 260;

export class FieldXpBar implements HudWidget {
  readonly root: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly level: HTMLElement;
  private readonly value: HTMLElement;
  private readonly segs: HTMLElement[] = [];
  private version = -1;
  private shown: XpState | null = null;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private active: boolean | null = null;
  private solo: boolean | null = null;
  private readonly hudRoot: HTMLElement;
  private docked = false;

  constructor(hudRoot: HTMLElement) {
    this.hudRoot = hudRoot;
    this.root = el('div', 'fx-xp hk-hit', hudRoot);
    const track = el('div', 'fx-xp-track', this.root);
    this.fill = el('div', 'fx-xp-fill', track);
    el('div', 'fx-xp-shine', track);
    const ticks = el('div', 'fx-xp-ticks', track);
    for (let i = 0; i < XP_SEGMENTS; i++) this.segs.push(el('i', 'fx-xp-seg', ticks));
    this.level = el('div', 'fx-xp-level hk-num', this.root);
    this.value = el('div', 'fx-xp-value hk-num', this.root);
  }

  update(): void {
    const v = fieldView;
    if (!this.docked) this.dock();
    if (v.active !== this.active) {
      this.active = v.active;
      this.root.classList.toggle('hk-hidden', !v.active);
      this.hudRoot.classList.toggle('hk-field', v.active);
    }
    // Alone on a field / in town: no party frames or meters (hud/field/field.css .hk-solo).
    const solo = v.active && v.mapKind !== 'dungeon';
    if (solo !== this.solo) {
      this.solo = solo;
      this.hudRoot.classList.toggle('hk-solo', solo);
    }
    if (!v.active || v.xpVersion === this.version) return;
    this.version = v.xpVersion;
    const cur: XpState = { level: v.level, xp: v.xp, next: v.next, maxed: v.maxed };
    const tw = xpTween(this.shown, cur);
    const reduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
    if (tw.rollovers > 0 && !reduced) {
      // Fill to the end, flash, empty (no transition), then fill to the new value.
      this.setFill(1, true);
      this.timers.push(
        setTimeout(() => {
          this.root.classList.remove('flash');
          void this.root.offsetWidth;
          this.root.classList.add('flash');
          this.setFill(0, false);
          this.draw(cur);
          this.timers.push(setTimeout(() => this.setFill(tw.to, true), 60));
        }, FILL_MS + 40),
      );
    } else {
      this.setFill(tw.to, !!this.shown);
      this.draw(cur);
    }
    this.shown = cur;
  }

  /** Move into the skill cluster, above the health / resource bars (cluster/skillCluster.ts .hkc-body). */
  private dock(): void {
    const body = this.hudRoot.querySelector('.hkc .hkc-body');
    if (!body?.parentElement) return;
    body.parentElement.insertBefore(this.root, body);
    this.root.classList.add('docked');
    this.docked = true;
  }

  private setFill(frac: number, animate: boolean): void {
    this.fill.style.transition = animate ? `transform ${FILL_MS}ms cubic-bezier(.3,.7,.3,1)` : 'none';
    this.fill.style.transform = `scaleX(${frac})`;
  }

  private draw(s: XpState): void {
    const view = xpBarView(s);
    setText(this.level, view.levelText);
    setText(this.value, view.pctText ? `${view.valueText} XP · ${view.pctText}` : view.valueText);
    this.root.title = view.title;
    for (let i = 0; i < this.segs.length; i++) this.segs[i]!.classList.toggle('lit', i < view.lit);
  }

  dispose(): void {
    for (const t of this.timers) clearTimeout(t);
    this.root.parentElement?.classList.remove('hk-field', 'hk-solo');
    this.root.remove();
  }
}
